import { Colli } from '@frontend/colli/types';
import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { Module } from '@frontend/module/types';
import { Package } from '@frontend/package/types';
import { SlotBadge } from '@frontend/slot/badge';
import { SpotRenderingClient } from '@frontend/spot/api';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { ChooseModuleModal } from '../../../components/choose-module-modal/choose-module-modal.component';
import { SpotLayout } from '../../../spot-rendering';
import { ConfirmOpenSlotHint } from '../../components';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import type { ColliConfirmOpenSlotsParallelViewProps } from '../../hooks/use-colli-confirm-open-slots-parallel';

interface ParallelConfirmOpenSlotsScreenProps {
    id: string;
    headerTitle: ReactNode;
    viewProps: ColliConfirmOpenSlotsParallelViewProps;
    openSlotsLabel: ReactNode;
}

const ColliSummary = ({ collis, packages }: { collis: Colli[]; packages: Package[] }) => {
    if (collis.length > 0) {
        return (
            <ul className='flex flex-col gap-2 self-stretch'>
                {collis.map((colli) => {
                    const pkg = packages.find((p) => p.colli_id === colli.id);
                    return (
                        <li
                            key={colli.id}
                            className='text-foreground-default flex flex-row items-start justify-between gap-2 text-base font-medium'>
                            <span className='flex flex-col'>
                                <span>
                                    {CommonMessage.OBJECTS.PARCEL.DEFAULT}
                                    {colli.type ? ` (${colli.type})` : ''}
                                </span>
                                {colli.label ? <span className='text-brand-muted text-xs'>{colli.label}</span> : null}
                            </span>
                            {pkg?.slot_id ? (
                                <SlotBadge
                                    id={pkg.slot_id}
                                    className='ms-0 ps-0'
                                />
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        );
    }

    if (packages.length > 0) {
        return (
            <div className='text-foreground-default text-base font-medium'>
                <FormattedMessage
                    id='kiosk.wizard.confirm-open-slots-parallel.packages-count'
                    defaultMessage='{count, plural, one {# parcel} other {# parcels}} ready to open'
                    description='Fallback count when collis are not seeded but packages are'
                    values={{ count: packages.length }}
                />
            </div>
        );
    }

    return null;
};

export const ParallelConfirmOpenSlotsScreen = (props: ParallelConfirmOpenSlotsScreenProps) => {
    const { viewProps } = props;

    if (!viewProps.accountId || !viewProps.spotId) {
        return (
            <WizardScreenContainer
                id={props.id}
                header={{ title: props.headerTitle }}
                containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-8 self-stretch overflow-y-scroll p-6 pb-20'>
                <Spinner />
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            id={props.id}
            header={{ title: props.headerTitle }}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled,
                submitLabel: props.openSlotsLabel
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-8 self-stretch overflow-y-scroll p-6 pb-20'>
            <div className='flex flex-col items-start gap-3 self-stretch'>
                {viewProps.resolveError ? (
                    <div className='text-foreground-default text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.confirm-open-slots-parallel.no-packages'
                            defaultMessage='No parcels are ready to open at this distriSPOT.'
                            description='Error when parallel confirm cannot resolve openable packages for collis'
                        />
                    </div>
                ) : (
                    <>
                        <ColliSummary
                            collis={viewProps.collis}
                            packages={viewProps.packages}
                        />
                        <ConfirmOpenSlotHint show={viewProps.selectedSlotIds.length > 0} />
                    </>
                )}
            </div>

            <SpotLayout<ModuleRendering, SlotRendering>
                renderMode='module'
                fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.accountId!, viewProps.spotId!, arg)}
                fetchModuleData={(accountId, spotId, moduleId) => SpotRenderingClient.fetchSpotModuleRendering(accountId, spotId, moduleId)}
                slot={{ renderElement: viewProps.slotComponent }}
                enableSlotHover={false}
                module={viewProps.currentModule || undefined}
                onModuleNameClick={() => viewProps.setSelectModuleModalOpen(true)}
                onSelectedModuleChange={(moduleId) => {
                    const module = viewProps.modules?.find((m: Module) => m.id === moduleId);
                    if (module) viewProps.setCurrentModule(module);
                }}
            />

            <ChooseModuleModal
                modules={viewProps.modules}
                show={viewProps.selectModuleModalOpen}
                onClose={() => viewProps.setSelectModuleModalOpen(false)}
                onSelectModule={viewProps.setCurrentModule}
            />
        </WizardScreenContainer>
    );
};
